// Role picker — "Which role are you working in?" (2026-09-04). Shown when a user with several roles has not chosen
// one yet, and from the role chip in the tech app header / desktop user menu to switch at any time.
// Concept design standard: navy title bar, crimson accent button.
import { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText, Typography,
} from '@mui/material';
import {
  Build as TechIcon, Headset as DispatchIcon, AdminPanelSettings as AdminIcon, Visibility as ViewerIcon, Star as SuperIcon, Badge as RoleIcon,
} from '@mui/icons-material';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { CC, GRAD } from '@/theme/theme';

export const roleIcon = (name: string) => {
  switch (name) {
    case 'Technician': return <TechIcon />;
    case 'Dispatcher': return <DispatchIcon />;
    case 'Admin': return <AdminIcon />;
    case 'SuperAdmin': return <SuperIcon />;
    case 'Viewer': return <ViewerIcon />;
    default: return <RoleIcon />;
  }
};

const roleBlurb: Record<string, string> = {
  Technician: 'My Day, tickets assigned to me, Tech Mail',
  Dispatcher: 'All tickets, schedule, Tech Inbox, office mail',
  Admin: 'Dashboard, tickets, team, settings',
  SuperAdmin: 'Everything',
  Viewer: 'Read-only view of tickets and the map',
};

export default function RolePickerDialog({ open, onClose, onChosen, forced = false }:
  { open: boolean; onClose: () => void; onChosen?: (roleName: string) => void; forced?: boolean }) {
  const { ws, setActiveRole } = useWorkspace();
  const [busy, setBusy] = useState<number | null>(null);
  const roles = ws?.roles || [];
  const current = ws?.active_role?.id;

  const choose = async (id: number, name: string) => {
    setBusy(id);
    try { await setActiveRole(id); onChosen?.(name); onClose(); }
    catch (_) { /* keep dialog open */ }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onClose={forced ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: GRAD.sidebar || CC.navy, color: '#fff', font: `600 16px ${CC.fontBody}`, py: 1.5 }}>
        Which role are you working in?
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
          You can do everything any of your roles allows. The role you pick sets your home screen, the bottom bar and which mailboxes open first — switch any time from the role chip.
        </Typography>
        <List sx={{ pt: 1 }}>
          {roles.map((r) => (
            <ListItemButton key={r.id} onClick={() => choose(r.id, r.name)} disabled={busy !== null} selected={r.id === current}
              sx={{ py: 1.25, '&.Mui-selected': { background: 'rgba(30,74,107,.08)' } }}>
              <ListItemIcon sx={{ color: r.id === current ? CC.red : CC.navy }}>{roleIcon(r.name)}</ListItemIcon>
              <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span style={{ fontWeight: 700 }}>{r.name}</span>{r.id === current && <Chip label="current" size="small" sx={{ height: 18, fontSize: 10 }} />}</Box>}
                secondary={roleBlurb[r.name] || r.description || ''} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      {!forced && <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>}
    </Dialog>
  );
}

/** Small header chip showing the active role; click to switch. Renders nothing for single-role users. */
export function RoleChip({ light = true }: { light?: boolean }) {
  const { ws, activeRoleName } = useWorkspace();
  const [open, setOpen] = useState(false);
  if (!ws || ws.roles.length < 2) return null;
  return (
    <>
      <Chip icon={roleIcon(activeRoleName)} label={activeRoleName || 'Choose role'} size="small" onClick={() => setOpen(true)}
        sx={{ height: 26, fontWeight: 700, fontSize: 12, cursor: 'pointer',
          ...(light ? { color: '#fff', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)', '& .MuiChip-icon': { color: '#fff', fontSize: 16 } }
                    : { color: CC.navy, background: 'rgba(30,74,107,.08)', '& .MuiChip-icon': { color: CC.navy, fontSize: 16 } }) }} />
      <RolePickerDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
